import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { ArticleHero } from "../ArticleHero";
import homeSessionHero from "../../assets/images/newborn-twins-heart-prop-wide-hero.webp";
import parentsByWindow from "../../assets/images/parents-holding-swaddled-newborn-family-portrait.webp";
import familyAtHome from "../../assets/images/parents-sibling-newborn-family-portrait.webp";
import newbornAwakeImage from "../../assets/images/newborn-awake-pink-wrap-portrait.webp";
import newbornGreenImage from "../../assets/images/newborn-green-stroller-portrait.webp";
import newbornSafetyImage from "../../assets/images/newborn-photography-safety-supported-pose.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { GuideCtaActions } from "./components/GuideCtaActions";
import { UiIcon } from "./components/UiIcons";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./InHomeNewbornPhotography.scss";

const photographerBrings = [
  "Professional lighting selected for the room and the photographs we plan to create",
  "Wraps, simple styling pieces and posing accessories included with your chosen package",
  "Protective floor coverings and the equipment needed for a calm, organized setup",
  "A flexible plan that changes with your baby’s feeding, sleep and comfort",
];

const familyPrepares = [
  "One clear working area near an outlet; the entire home does not need to be photo-ready",
  "Feeding and changing supplies within easy reach",
  "Comfortable clothing for parents, siblings and the baby",
  "A familiar pacifier, blanket or soothing item if your baby uses one",
];

const sessionSteps = [
  {
    number: "01",
    title: "Before the session",
    description:
      "We discuss your home, preferred portraits, family members and practical details so I arrive with the right plan.",
  },
  {
    number: "02",
    title: "When I arrive",
    description:
      "I walk through the available rooms, look at the light and choose the most comfortable working area with you.",
  },
  {
    number: "03",
    title: "During baby portraits",
    description:
      "Feeding, changing and soothing breaks happen whenever they are needed. The pace follows your newborn.",
  },
  {
    number: "04",
    title: "Family moments",
    description:
      "Parents and siblings join in with simple direction, using the parts of your home that photograph most naturally.",
  },
];

const relatedGuides = [
  {
    title: "Best Age for Newborn Photos",
    href: "/resources/newborn/best-age-for-newborn-photos/",
    image: newbornAwakeImage,
    imageAlt: "Awake newborn baby wrapped in soft pink fabric",
  },
  {
    title: "What Happens During a Newborn Session",
    href: "/resources/newborn/what-happens-during-a-newborn-session/",
    image: newbornGreenImage,
    imageAlt: "Sleeping newborn baby posed in a green wooden stroller",
  },
  {
    title: "Is Newborn Photography Safe?",
    href: "/resources/newborn/newborn-photography-safety/",
    image: newbornSafetyImage,
    imageAlt: "Newborn baby supported by adult hands during a posed portrait",
  },
];

export const InHomeNewbornPhotography = (): ReactElement => (
  <main className="newborn-page newborn-article-page in-home-newborn-guide">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Newborn", to: "/resources/newborn/" },
          { label: "In-Home Newborn Photography" },
        ]}
        category="In-home newborn guide"
        description="A practical look at space, lighting, equipment and family portraits when your newborn session takes place in the comfort of home."
        imageAlt="Sleeping newborn twins wrapped in neutral blankets inside a heart-shaped prop"
        imagePosition="center 48%"
        imageSrc={homeSessionHero}
        readingTime="8 min read"
        title="In-Home Newborn Photography: What Parents Should Know"
        updatedAt="2026-08-29"
        updatedLabel="Published Aug 29, 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout in-home-newborn-answer">
          <span className="newborn-article-callout__icon">
            <UiIcon name="home" />
          </span>
          <div>
            <h2>Your home does not need to look like a studio</h2>
            <p>
              You do not need perfect window light, a large nursery or an empty living room. We choose one practical
              working area, and I bring the experience and equipment needed to create a polished newborn gallery in the
              place where your family is already most comfortable.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#home-ready">Does my home need to be photo-ready?</a>
            </li>
            <li>
              <a href="#bring-prepare">What I bring and what you prepare</a>
            </li>
            <li>
              <a href="#space-light">How much space and light we need</a>
            </li>
            <li>
              <a href="#session-flow">How an in-home session flows</a>
            </li>
            <li>
              <a href="#family-portraits">Parents and siblings in the photographs</a>
            </li>
            <li>
              <a href="#in-home-faq">In-home newborn photography FAQ</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section in-home-newborn-intro" id="home-ready">
          <div>
            <p className="newborn-article-kicker">COMFORT, WITHOUT THE COMMUTE</p>
            <h2>A newborn session built around real family life</h2>
            <p>
              An in-home session means your baby can be fed, changed and soothed with familiar things close by. You
              avoid packing the family into the car, and older siblings can take a break in their own space when they
              are not being photographed.
            </p>
            <p>
              We may create professionally posed newborn portraits, relaxed lifestyle photographs or a blend of both,
              depending on the package you choose and what works naturally in your home.
            </p>
          </div>
          <ArticlePhoto
            alt="Mother and father holding their swaddled newborn during an in-home family portrait"
            className="in-home-newborn-intro__photo"
            label="IN-HOME FAMILY PORTRAIT"
            src={parentsByWindow}
          />
        </section>

        <section className="in-home-newborn-prep" id="bring-prepare">
          <header>
            <p className="newborn-article-kicker">A SIMPLE DIVISION OF RESPONSIBILITY</p>
            <h2>What I bring. What you prepare.</h2>
            <p>
              Most of the technical preparation belongs to the photographer. Your part is intentionally small and
              focused on keeping the baby and family comfortable.
            </p>
          </header>
          <div className="in-home-newborn-prep__grid">
            <article>
              <span>PHOTOGRAPHER</span>
              <h3>I bring the session to you</h3>
              <ul>
                {photographerBrings.map((item) => (
                  <li key={item}>
                    <UiIcon name="check" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
            <article>
              <span>FAMILY</span>
              <h3>You prepare the familiar comforts</h3>
              <ul>
                {familyPrepares.map((item) => (
                  <li key={item}>
                    <UiIcon name="check" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="newborn-article-section in-home-newborn-space" id="space-light">
          <header>
            <p className="newborn-article-kicker">ONE GOOD WORKING AREA IS ENOUGH</p>
            <h2>How much space and natural light do we need?</h2>
            <p>
              I do not expect every room to be bright or uncluttered. When I arrive, we look at the available space
              together and use the area that supports the session best.
            </p>
          </header>
          <div>
            <article>
              <strong>Space</strong>
              <p>A living room, bedroom or open area can work once a small amount of furniture is moved if needed.</p>
            </article>
            <article>
              <strong>Light</strong>
              <p>
                Window light is beautiful when available; professional lighting can provide consistency when it is not.
              </p>
            </article>
            <article>
              <strong>Temperature</strong>
              <p>We adjust the room with the baby’s clothing, wraps and comfort in mind throughout the session.</p>
            </article>
          </div>
        </section>

        <section className="newborn-article-section in-home-newborn-flow" id="session-flow">
          <header>
            <p className="newborn-article-kicker">FROM ARRIVAL TO FAMILY PORTRAITS</p>
            <h2>What an in-home newborn session really looks like</h2>
          </header>
          <ol>
            {sessionSteps.map((step) => (
              <li key={step.number}>
                <span>{step.number}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="newborn-article-section in-home-newborn-family" id="family-portraits">
          <ArticlePhoto
            alt="Parents and older brother posing with their newborn baby during an in-home family portrait"
            className="in-home-newborn-family__photo"
            label="FAMILY PORTRAIT AT HOME"
            src={familyAtHome}
          />
          <div>
            <p className="newborn-article-kicker">YOUR FAMILY BELONGS IN THE STORY</p>
            <h2>Parents and siblings do not need to know how to pose</h2>
            <p>
              I guide hands, body position and where to look, while keeping the interaction natural. Parent portraits
              can be made in a bedroom, near a window or against a simple wall; the room only needs to support the
              composition—not become the subject of the photograph.
            </p>
            <p>
              Sibling photographs are planned around age and attention span. We photograph those moments when the older
              child is ready rather than asking them to wait through the full newborn session.
            </p>
          </div>
        </section>

        <section className="newborn-article-section in-home-newborn-faq" id="in-home-faq">
          <header>
            <p className="newborn-article-kicker">HELPFUL ANSWERS</p>
            <h2>In-home newborn photography FAQ</h2>
          </header>
          <div className="in-home-newborn-faq__columns">
            <div className="in-home-newborn-faq__column">
              <details className="in-home-newborn-faq__item in-home-newborn-faq__item--1">
                <summary>Do I need to clean my entire house?</summary>
                <p>
                  No. Clear the surfaces you would like photographed and leave the rest. We only use selected areas.
                </p>
              </details>
              <details className="in-home-newborn-faq__item in-home-newborn-faq__item--3">
                <summary>Can we include pets?</summary>
                <p>
                  Often, yes. Tell me in advance so we can plan a safe, brief setup based on your pet’s temperament and
                  comfort around the baby.
                </p>
              </details>
            </div>
            <div className="in-home-newborn-faq__column">
              <details className="in-home-newborn-faq__item in-home-newborn-faq__item--2">
                <summary>What if my home is dark?</summary>
                <p>
                  I assess the available light and use professional lighting when needed. A dark home does not
                  automatically prevent a polished session.
                </p>
              </details>
              <details className="in-home-newborn-faq__item in-home-newborn-faq__item--4">
                <summary>What if the baby needs to feed or will not sleep?</summary>
                <p>
                  We pause whenever needed. Awake portraits, feeding breaks and cuddles are normal parts of a baby-led
                  in-home session.
                </p>
              </details>
            </div>
          </div>
        </section>

        <section className="newborn-article-cta">
          <div>
            <p className="newborn-article-kicker">NEWBORN PHOTOGRAPHY IN YOUR HOME</p>
            <h2>Choose the in-home experience that fits your family</h2>
            <p>
              Compare lifestyle and professionally posed newborn options, see what each package includes and contact
              Alena if you would like help choosing.
            </p>
          </div>
          <GuideCtaActions category="newborn" />
        </section>

        <section className="newborn-related-guides">
          <header>
            <h2>Related newborn guides</h2>
            <Link to="/resources/newborn/">
              View all guides <UiIcon name="arrow" />
            </Link>
          </header>
          <div>
            {relatedGuides.map((guide, index) => (
              <Link className="newborn-related-guide" key={guide.href} to={guide.href}>
                <ArticlePhoto
                  alt={guide.imageAlt}
                  className={`newborn-related-guide__photo newborn-related-guide__photo--${index + 1}`}
                  label="PHOTO"
                  src={guide.image}
                />
                <span>{guide.title}</span>
                <UiIcon name="arrow" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  </main>
);

export default InHomeNewbornPhotography;
